import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, MapPin, Clock, Users, Printer, Trash2, Pencil, MessageCircle, ExternalLink } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";
import JobForm from "@/components/app/JobForm";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import { toast } from "sonner";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    api.get("/jobs", { params }).then((r) => setJobs(r.data));
  };

  useEffect(load, [status]);
  useAutoRefresh(load, 20000);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return jobs;
    return jobs.filter(
      (j) =>
        j.client_name.toLowerCase().includes(term) ||
        j.address_from.toLowerCase().includes(term) ||
        j.address_to.toLowerCase().includes(term)
    );
  }, [jobs, q]);

  const handleDelete = async () => {
    try {
      await api.delete(`/jobs/${confirmDel.id}`);
      toast.success("Lavoro eliminato");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Operazioni</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Lavori</h1>
          <p className="text-slate-500 mt-2 text-sm">Crea, modifica e assegna i traslochi ai dipendenti.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          className="bg-sky-600 hover:bg-sky-700 text-white shadow-md shadow-sky-600/30 h-11"
          data-testid="create-job-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuovo Lavoro
        </Button>
      </header>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per cliente o indirizzo…"
            className="pl-10 h-11 bg-white"
            data-testid="jobs-search-input"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-60 h-11 bg-white" data-testid="filter-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="programmato">Programmato</SelectItem>
            <SelectItem value="in_corso">In Corso</SelectItem>
            <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center">
          <div className="text-slate-400 text-lg font-display font-bold mb-1">Nessun lavoro trovato</div>
          <div className="text-slate-500 text-sm">Crea il tuo primo trasloco con il pulsante in alto.</div>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="jobs-list">
          {filtered.map((j) => (
            <div
              key={j.id}
              className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-300 hover:shadow-md transition group"
              data-testid={`job-card-${j.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <Link to={`/lavori/${j.id}`} className="min-w-0 block">
                  <div className="font-display font-bold text-lg text-slate-900 truncate">{j.client_name}</div>
                  <div className="font-mono text-xs text-slate-400 mt-1 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> {j.scheduled_date} · {j.start_time}{j.end_time && ` – ${j.end_time}`}
                  </div>
                </Link>
                <StatusBadge status={j.status} />
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" /><span className="truncate">{j.address_from}</span></div>
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><span className="truncate">{j.address_to}</span></div>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-500">
                <Users className="w-3.5 h-3.5" />
                {(j.assigned_employees || []).length > 0
                  ? (j.assigned_employees || []).map((e) => e.name).join(", ")
                  : <span className="italic text-slate-400">Nessun dipendente</span>}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <Link
                  to={`/lavori/${j.id}`}
                  className="text-sm font-semibold text-sky-600 hover:text-sky-700"
                  data-testid={`view-job-${j.id}`}
                >
                  Vedi dettagli →
                </Link>
                <div className="flex gap-1">
                  <Link
                    to={`/stampa/${j.id}`}
                    target="_blank"
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                    title="Stampa foglio di lavoro"
                    data-testid={`print-job-${j.id}`}
                  >
                    <Printer className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => { setEditing(j); setOpen(true); }}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                    title="Modifica"
                    data-testid={`edit-job-${j.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDel(j)}
                    className="p-2 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600"
                    title="Elimina"
                    data-testid={`delete-job-${j.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">
              {editing ? "Modifica Lavoro" : "Nuovo Lavoro"}
            </DialogTitle>
          </DialogHeader>
          <JobForm
            job={editing}
            onCancel={() => setOpen(false)}
            onDone={() => {
              setOpen(false);
              load();
              toast.info("Lavoro salvato. Quando è pronto, apri la scheda e tocca 'Invia a dipendenti' per inviarglielo.");
            }}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo lavoro?</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione è irreversibile. Il lavoro per <b>{confirmDel?.client_name}</b> verrà rimosso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-job"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
